import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCLocationApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-location.api-model';
import {CpmCLocationApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-location.api-service';
import {
    CpmCLocationCreateApiSchema,
    CpmCLocationUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-location.api-schemas';
import {
    ECpmCLocationTypeApiEnum,
    ECpmCLocationVectorTypeApiEnum,
    RECpmCLocationTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-location.api-enums';
import {CpmCLocationApiUtils} from '../../../../gen/cpm-c-api-ts/src/utils/c-customer/cpmc-location.api-utils';
import {TreeNode} from 'primeng/api';
import {LocationUtils} from '../../utils/location.util';
import {CustomerUrlBuilderUtil} from '../../utils/customer-url-builder.util';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {StringUtils} from '../../../../sdk/src/utils/string.util';
import {CpmCLocationTreeApiView} from '../../../../gen/cpm-c-api-ts/src/views/c-customer/cpmc-location-tree.api-view';

export class NewEditLocationModalConfig {
    constructor(
        public existing?: CpmCLocationApiModel,
        public initialType?: ECpmCLocationTypeApiEnum,
        public initialParentUUID?: string,
        public organizationUUID?: string
    ) {}
}

export class NewEditLocationModalResult {
    constructor(public item?: CpmCLocationApiModel) {}
}

/**
 * New/Edit Location modal
 */
@Component({
    selector: 'app-new-edit-location-modal',
    templateUrl: './new-edit-location-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditLocationModalComponent extends IBaseCRUModalComponent<
    CpmCLocationApiModel,
    NewEditLocationModalComponent,
    NewEditLocationModalConfig,
    NewEditLocationModalResult
> {
    protected readonly CPMLocationApiUtils = CpmCLocationApiUtils;
    protected readonly LocationUtils = LocationUtils;
    protected readonly ECPMLocationVectorTypeApiEnum = ECpmCLocationVectorTypeApiEnum;

    protected item?: CpmCLocationApiModel;
    protected typeOfLocationOptions = Object.keys(ECpmCLocationTypeApiEnum).map((key) => ({
        label: StringUtils.toHumanFormatedStr(key),
        value: RECpmCLocationTypeApiEnum[key]
    }));
    protected parentsTreeRoot: TreeNode<CpmCLocationTreeApiView> = {children: []};
    protected currentParentUUID?: string;

    // ===============================================================
    // ===============================================================

    private initialLocationsLoading = true;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.goTo = () => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate([
                CustomerUrlBuilderUtil.customerOrganizationHierarchyLocation(
                    customerUUID!,
                    this.item!.organization_uuid,
                    this.item?.uuid
                )
            ]);
        };

        this.configure('LocationApiModel', (model: CpmCLocationApiModel) => model.name ?? '<Unknown location>');
        this.config.width = '60rem';
        this.contentMinHeight = '28rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            type: new FormControl<string | null>(
                this.config.data?.extended?.initialType ?? ECpmCLocationTypeApiEnum.REGION,
                [Validators.required]
            ),
            parent: new FormControl<TreeNode<CpmCLocationApiModel> | null>(null, [Validators.required]),
            name: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, []),
            x1: new FormControl<number>(0, []),
            y1: new FormControl<number>(0, []),
            z1: new FormControl<number>(0, []),
            x2: new FormControl<number>(0, []),
            y2: new FormControl<number>(0, []),
            z2: new FormControl<number>(0, []),
            slot_count: new FormControl<number>(0, [])
        });

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading the location...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this.item = await firstValueFrom(
                        CpmCLocationApiService.getLocationByUUID(
                            this.config.data?.extended?.existing?.uuid ?? '',
                            this.config.data?.extended?.organizationUUID
                        )
                    );
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the location...';

            if (!this.isCreationMode) {
                this.form.controls['type'].disable();
                this.form.controls['slot_count'].disable();
            }

            this.form.patchValue(this.item!);
            this.form.updateValueAndValidity();
        }

        await this.onTypeChanged();

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            const parentTreeNode = TreeUtils.findTreeNodeByKey(this.parentsTreeRoot.children!, this.item!.parent_uuid!);
            this.form.patchValue({parent: parentTreeNode});

            if (this.dialogOpenMode !== EDialogOpenMode.READ_ONLY)
                setInterval(() => {
                    this.form.controls['parent'].enable();
                }, 0);
        }

        // Update the form when the type change
        this.form.controls['type'].valueChanges.subscribe(async () => {
            if (this.form.controls['type']?.value == this.item?.type) return;

            this.showLoading = true;
            await this.onTypeChanged();
            this.showLoading = false;
        });
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmitCommon = {
            parent_uuid: LocationUtils.isGlobalLocation(this.item)
                ? undefined
                : this.form.controls['parent'].value?.key,
            name: this.form.controls['name'].value,
            description: this.form.controls['description'].value,
            x1: this.form.controls['x1']?.value ?? null,
            y1: this.form.controls['y1']?.value ?? null,
            z1: this.form.controls['z1']?.value ?? null,
            x2: this.form.controls['x2']?.value ?? null,
            y2: this.form.controls['y2']?.value ?? null,
            z2: this.form.controls['z2']?.value ?? null,
            slot_count: this.form.controls['slot_count']?.value ?? null
        };

        const toSubmit: CpmCLocationCreateApiSchema | CpmCLocationUpdateApiSchema = this.isCreationMode
            ? {...toSubmitCommon, type: this.form.controls['type'].value}
            : toSubmitCommon;

        await this.submitCU(
            null,
            CpmCLocationApiService.createLocation,
            CpmCLocationApiService.updateLocation,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCLocationApiModel) => new NewEditLocationModalResult(resultCRUD)
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Update validators for a form control based on a condition
     */
    private updateControlValidators(controlName: string, isRequired: boolean): void {
        const control = this.form.controls[controlName];
        if (isRequired) {
            control.setValidators([Validators.required]);
        } else {
            control.clearValidators();
        }
        control.updateValueAndValidity();
    }

    /**
     * Update all vector validators based on location type
     */
    private updateVectorValidators(locationType: ECpmCLocationTypeApiEnum): void {
        // Vector 1
        const vec1Type = CpmCLocationApiUtils.getVector1Type(locationType);
        const vec1Specs = CpmCLocationApiUtils.getVectorTypeComponentSpec(vec1Type, locationType, 1);

        this.updateControlValidators('x1', vec1Specs.components[0].enabled);
        this.updateControlValidators('y1', vec1Specs.components[1].enabled);
        this.updateControlValidators('z1', vec1Specs.components[2].enabled);

        // Vector 2
        const vec2Type = CpmCLocationApiUtils.getVector2Type(locationType);
        const vec2Specs = CpmCLocationApiUtils.getVectorTypeComponentSpec(vec2Type, locationType, 2);

        this.updateControlValidators('x2', vec2Specs.components[0].enabled);
        this.updateControlValidators('y2', vec2Specs.components[1].enabled);
        this.updateControlValidators('z2', vec2Specs.components[2].enabled);
    }

    /**
     * Update parent validator
     */
    private updateParentValidator(): void {
        if (LocationUtils.isGlobalLocation(this.item) && this.dialogOpenMode === EDialogOpenMode.EDIT) {
            this.form.controls['parent'].clearValidators();
        } else {
            this.form.controls['parent'].setValidators([Validators.required]);
        }
        this.form.controls['parent'].updateValueAndValidity({emitEvent: false});
    }

    /**
     * Load and build the parent location tree
     */
    private async loadParentLocationTree(locationType: ECpmCLocationTypeApiEnum): Promise<void> {
        this.parentsTreeRoot.children = [];

        const locationTree = await firstValueFrom(
            CpmCLocationApiService.getLocationTree(
                {
                    downToType: CpmCLocationApiUtils.getParentType(locationType)
                },
                CpmCLocationApiService.dPaginatorConfig,
                this.config.data?.extended?.organizationUUID
            )
        );

        let filteredLocations = locationTree.data;

        // Region case : remove own organization and other region children in parent affectation (avoid infinite loop)
        if (!this.isCreationMode && this.item?.uuid && this.item.type === ECpmCLocationTypeApiEnum.REGION) {
            const uuidsToExclude = new Set<string>();
            uuidsToExclude.add(this.item.uuid);

            const findDescendants = (parentUuid: string) => {
                locationTree.data.forEach((loc) => {
                    if (loc.parent_uuid === parentUuid) {
                        uuidsToExclude.add(loc.uuid);
                        findDescendants(loc.uuid);
                    }
                });
            };

            findDescendants(this.item.uuid);

            filteredLocations = locationTree.data.filter((loc) => {
                return !uuidsToExclude.has(loc.uuid);
            });
        }

        LocationUtils.buildLocationHierarchyTree(
            filteredLocations,
            this.parentsTreeRoot,
            this.isCreationMode ? [ECpmCLocationTypeApiEnum.REGION, ECpmCLocationTypeApiEnum.SITE] : [],
            [CpmCLocationApiUtils.getParentType(locationType)!]
        );

        if (!this.isCreationMode) {
            TreeUtils.treeNodesExpandByKeys(
                this.parentsTreeRoot.children,
                this.item!.parent_path.split('/').filter((x) => x.length > 0)
            );
            this.currentParentUUID = this.item!.parent_uuid;
        }

        if (this.initialLocationsLoading && this.config.data?.extended?.initialParentUUID) {
            this.form.controls['parent'].setValue(
                TreeUtils.findTreeNodeByKey(this.parentsTreeRoot.children, this.config.data.extended.initialParentUUID)
            );
        }
        this.initialLocationsLoading = false;
    }

    /**
     * When the type is changed
     */
    private async onTypeChanged(): Promise<void> {
        const locationType = this.form.controls['type'].value;

        // Update parent validator
        this.updateParentValidator();

        // Update vector validators
        this.updateVectorValidators(locationType);

        // Update slot count validator
        this.updateControlValidators('slot_count', CpmCLocationApiUtils.hasSlotCount(locationType));

        // Update form validity
        this.form.updateValueAndValidity();

        // Load parent location tree
        await this.loadParentLocationTree(locationType);
    }
}
